import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { comOperador } from '../lib/utils';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import { Avatar, AvatarFallback } from './ui/avatar';
import MinhasDemandas from './rodizio/MinhasDemandas';
import RodizioResumo from './rodizio/RodizioResumo';
import { saudacaoDaHora } from './atendimento/textReplacer';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import {
  LayoutDashboard,
  Plus,
  List,
  RotateCcw,
  Upload,
  Menu,
  X,
  Sun,
  Moon,
  LogOut,
  User,
  ChevronLeft,
  FileText,
  Bell,
  Check,
  PackageOpen,
  CreditCard,
  XCircle,
  Factory,
  FileWarning,
  Search,
  ShoppingCart,
  PackagePlus,
  ExternalLink,
  Truck
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Blocos do menu (definidos pela gestão): Gestão | Atendimento | Demais atividades
const NAV_GROUPS = ['Gestão', 'Atendimento', 'Demais atividades'];

const navItems = [
  { path: '/importar', label: 'Base ELO', icon: Upload, group: 'Gestão' },
  { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, group: 'Gestão' },
  { path: '/chamados/novo', label: 'Novo Atendimento', icon: Plus, group: 'Atendimento' },
  { path: '/chamados', label: 'Atendimentos', icon: List, group: 'Atendimento' },
  { path: '/textos-atendimento', label: 'Textos Atendimento', icon: FileText, group: 'Atendimento' },
  { href: 'https://compras.wct360.com.br/tem-em-casa', label: 'Tem em Casa', icon: PackagePlus, group: 'Demais atividades', external: true },
  { path: '/retirada', label: 'Disp. p/ Retirada', icon: PackageOpen, group: 'Demais atividades' },
  { path: '/pagamento', label: 'Pag. Não Aprovado', icon: CreditCard, group: 'Demais atividades' },
  { path: '/cancelamentos', label: 'Cancelamentos (AES)', icon: XCircle, group: 'Demais atividades' },
  { path: '/etr', label: 'Falta na Produção (ETR)', icon: Factory, group: 'Demais atividades' },
  { path: '/pendencias-transportadora', label: 'Pendência Transportadora', icon: Truck, group: 'Demais atividades' },
  { path: '/erro-nota', label: 'Erro na Nota', icon: FileWarning, group: 'Demais atividades' },
  { path: '/avisos-compras', label: 'Avisos de Compras', icon: ShoppingCart, group: 'Demais atividades' },
  { path: '/busca-produto', label: 'Buscar Produto', icon: Search, group: 'Demais atividades' },
];

// Marca da WeConnect (Adnéia, 18/10/2026: o logo certo são as duas barras laranja com a
// bolinha azul-marinho). Em vetor, para ficar nítida; no menu escuro a bolinha fica branca.
const MarcaWeConnect = ({ altura = 28, bolinha = '#14194A' }) => (
  <svg width={Math.round((altura * 240) / 158)} height={altura} viewBox="0 0 240 158"
    role="img" aria-label="WeConnect" className="flex-shrink-0">
    <path d="M30 25 L80 130 M123 25 L170 130" fill="none" stroke="#F15A22" strokeWidth="46" strokeLinecap="round" />
    <circle cx="213" cy="23" r="21" fill={bolinha} />
  </svg>
);

// "Olá Ad, bom dia!" no topo (Adnéia, 18/10/2026). Apelido igual ao das mensagens do rodízio.
const APELIDO = { adneia: 'Ad', gabriela: 'Gabi' };
const nomeCurto = (nome) => {
  const p = String(nome || '').trim().split(/\s+/)[0] || '';
  const chave = p.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
  return APELIDO[chave] || p;
};

export const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [notificacoes, setNotificacoes] = useState([]);
  const [notificacoesNaoLidas, setNotificacoesNaoLidas] = useState(0);
  const [avisosPendentes, setAvisosPendentes] = useState(0);
  const [avisosIds, setAvisosIds] = useState([]);      // ids dos avisos ativos atuais
  const [avisosVistos, setAvisosVistos] = useState([]); // ids que o usuário já "olhou"
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, getAuthHeader, isDashboardOnly, isConsulta } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const [notificacaoSelecionada, setNotificacaoSelecionada] = useState(null);
  // "Ver todas" as notificações (modal com a lista completa)
  const [verTodasOpen, setVerTodasOpen] = useState(false);
  const [todasNotifs, setTodasNotifs] = useState([]);
  const [todasTotal, setTodasTotal] = useState(0);
  const [filtroNaoLidas, setFiltroNaoLidas] = useState(false);
  const [carregandoTodas, setCarregandoTodas] = useState(false);
  // Relógio da saudação: troca sozinha de bom dia para boa tarde e boa noite.
  const [agora, setAgora] = useState(() => new Date());
  useEffect(() => {
    const t = setInterval(() => setAgora(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  const abrirVerTodas = async (soNaoLidas = filtroNaoLidas) => {
    setVerTodasOpen(true);
    setCarregandoTodas(true);
    try {
      const r = await axios.get(
        `${API_URL}/api/notificacoes?limit=2000${soNaoLidas ? '&apenas_nao_lidas=1' : ''}`,
        { headers: getAuthHeader() }
      );
      setTodasNotifs(r.data.notificacoes || []);
      setTodasTotal(r.data.total || 0);
      setNotificacoesNaoLidas(r.data.nao_lidas || 0);
    } catch {
      setTodasNotifs([]);
    } finally {
      setCarregandoTodas(false);
    }
  };

  // Buscar notificações ao carregar
  useEffect(() => {
    const fetchNotificacoes = async () => {
      try {
        const response = await axios.get(
          `${API_URL}/api/notificacoes`,
          { headers: getAuthHeader() }
        );
        setNotificacoes(response.data.notificacoes || []);
        setNotificacoesNaoLidas(response.data.nao_lidas || 0);
      } catch (error) {
        console.error('Erro ao buscar notificações:', error);
      }
      try {
        const av = await axios.get(`${API_URL}/api/avisos-compras`, { headers: getAuthHeader() });
        const arr = av.data?.avisos || [];
        setAvisosPendentes(av.data?.total || 0);
        setAvisosIds(arr.map(a => a.id).filter(Boolean));
      } catch (error) {
        // contador do menu é best-effort — silencioso
      }
    };

    if (user) {
      fetchNotificacoes();
      // Atualizar a cada 60 segundos
      const interval = setInterval(fetchNotificacoes, 60000);
      return () => clearInterval(interval);
    }
  }, [user, getAuthHeader]);

  // Chave do localStorage com os avisos já "vistos" (por usuário)
  const _avisosVistosKey = `avisos_vistos_${user?.email || 'anon'}`;

  // Carrega os avisos já vistos quando o usuário é conhecido
  useEffect(() => {
    if (!user) return;
    try {
      setAvisosVistos(JSON.parse(localStorage.getItem(`avisos_vistos_${user.email || 'anon'}`) || '[]'));
    } catch {
      setAvisosVistos([]);
    }
  }, [user]);

  // Badge do menu = avisos AINDA NÃO vistos (some quando o usuário olha; volta com avisos novos)
  const avisosNaoVistos = avisosIds.filter(id => !avisosVistos.includes(id));
  const avisosBadge = avisosNaoVistos.length;

  // Marca todos os avisos atuais como vistos → some o badge (persiste no localStorage)
  const marcarAvisosVistos = (e) => {
    if (e) { e.preventDefault(); e.stopPropagation(); }
    setAvisosVistos(avisosIds.slice());
    try { localStorage.setItem(_avisosVistosKey, JSON.stringify(avisosIds)); } catch {}
  };

  // "Olhar" = abrir a página de Avisos de Compras também marca como vistos
  useEffect(() => {
    if (location.pathname === '/avisos-compras' && avisosIds.length > 0) {
      setAvisosVistos(avisosIds.slice());
      try { localStorage.setItem(`avisos_vistos_${user?.email || 'anon'}`, JSON.stringify(avisosIds)); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, avisosIds]);

  // Marcar notificação como lida
  const marcarComoLida = async (notificacaoId) => {
    try {
      await axios.put(
        `${API_URL}/api/notificacoes/${notificacaoId}/lida`,
        {},
        { headers: getAuthHeader() }
      );
      setNotificacoes(prev => prev.map(n =>
        n.id === notificacaoId ? { ...n, lida: true } : n
      ));
      setNotificacoesNaoLidas(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Erro ao marcar notificação como lida:', error);
    }
  };

  // Marcar todas como lidas
  const marcarTodasComoLidas = async () => {
    try {
      await axios.put(
        `${API_URL}/api/notificacoes/marcar-todas-lidas`,
        {},
        { headers: getAuthHeader() }
      );
      setNotificacoes(prev => prev.map(n => ({ ...n, lida: true })));
      setNotificacoesNaoLidas(0);
    } catch (error) {
      console.error('Erro ao marcar notificações como lidas:', error);
    }
  };

  // Verificar se é ambiente de teste (preview)
  const isPreview = window.location.hostname.includes('preview.emergentagent.com');
  const appTitle = isPreview ? 'ELO - Ambiente de Teste' : 'ELO - Sistema de Atendimentos';

  // Filtrar itens de navegação baseado no usuário
  const filteredNavItems = navItems.filter(item => {
    if (isDashboardOnly) {
      return item.path === '/dashboard';
    }
    // Perfil consulta: apenas Dashboard, Atendimentos, Pag. Não Aprovado e Cancelamentos
    if (isConsulta) {
      return ['/dashboard', '/chamados', '/pagamento', '/cancelamentos',
              '/etr', '/erro-nota'].includes(item.path);
    }
    if (item.adminOnly) {
      return user?.email === 'adneia@weconnect360.com.br';
    }
    return true;
  });

  // Agrupa os itens visíveis nos blocos, na ordem definida (grupo vazio some).
  const groupedNav = NAV_GROUPS
    .map(g => ({ group: g, items: filteredNavItems.filter(i => i.group === g) }))
    .filter(g => g.items.length > 0);

  const renderNavItem = (item, mobile = false) => {
    const Icon = item.icon;
    const showLabel = mobile || sidebarOpen;
    if (item.external) {
      return (
        <a
          key={item.href}
          href={comOperador(item.href, user?.name)}
          target="_blank"
          rel="noopener noreferrer"
          {...(mobile ? { onClick: () => setMobileMenuOpen(false) } : {})}
          data-testid="nav-subir-pedido"
          title="Abre o Tem em Casa em uma nova aba (sistema de Compras)"
          className="wc-nav"
        >
          <Icon className="h-5 w-5 flex-shrink-0" />
          {showLabel && <span>{item.label}</span>}
          {showLabel && <ExternalLink className="h-3.5 w-3.5 ml-auto opacity-60 flex-shrink-0" />}
        </a>
      );
    }
    const isActive = mobile
      ? location.pathname === item.path
      : (location.pathname === item.path ||
         (item.path === '/chamados' && location.pathname.startsWith('/chamados/') && item.path !== '/chamados/novo'));
    const showBadge = item.path === '/avisos-compras' && avisosBadge > 0 && (mobile || sidebarOpen);
    return (
      <Link
        key={item.path}
        to={item.path}
        {...(mobile ? { onClick: () => setMobileMenuOpen(false) } : {})}
        data-testid={`nav-${item.path.replace(/\//g, '-')}`}
        className={`wc-nav ${isActive ? 'wc-active' : ''}`}
      >
        <Icon className="h-5 w-5 flex-shrink-0" />
        {showLabel && <span>{item.label}</span>}
        {showBadge && (
          <span
            role="button"
            title="Clique para marcar como visto"
            onClick={marcarAvisosVistos}
            className="ml-auto inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-red-500 hover:bg-red-600 text-white text-[11px] font-bold cursor-pointer transition-colors"
          >
            {avisosBadge > 99 ? '99+' : avisosBadge}
          </span>
        )}
      </Link>
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside
        className={`hidden md:flex flex-col wc-side border-r transition-all duration-300 ${
          sidebarOpen ? 'w-64' : 'w-16'
        }`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          {sidebarOpen && (
            <div className="flex items-center gap-2.5 min-w-0">
              <MarcaWeConnect altura={24} bolinha="#fff" />
              <span className="wc-elo-big">ELO<span className="wc-dot">.</span></span>
            </div>
          )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="toggle-sidebar-btn"
            className="h-8 w-8 flex-shrink-0 text-white/70 hover:text-white hover:bg-white/10"
          >
            <ChevronLeft className={`h-4 w-4 transition-transform ${!sidebarOpen ? 'rotate-180' : ''}`} />
          </Button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {groupedNav.map((grp, gi) => (
            <div key={grp.group} className={gi > 0 ? 'mt-3' : ''}>
              {sidebarOpen ? (
                <p className="wc-grp">
                  {grp.group}
                </p>
              ) : (
                gi > 0 && <div className="mx-2 my-2 border-t border-white/10" />
              )}
              <div className="space-y-1">
                {grp.items.map((item) => renderNavItem(item, false))}
              </div>
            </div>
          ))}
        </nav>

        {/* User section */}
        <div className="p-3 border-t border-white/10">
          {sidebarOpen ? (
            <div className="flex items-center gap-3">
              <Avatar className="h-9 w-9">
                <AvatarFallback className="bg-[#EE6B1F] text-white text-xs">
                  {getInitials(user?.name)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate text-white">{user?.name}</p>
                <p className="text-xs truncate text-white/50">{user?.email}</p>
              </div>
            </div>
          ) : (
            <Avatar className="h-9 w-9 mx-auto">
              <AvatarFallback className="bg-[#EE6B1F] text-white text-xs">
                {getInitials(user?.name)}
              </AvatarFallback>
            </Avatar>
          )}
        </div>
      </aside>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 w-64 wc-side border-r z-50 transform transition-transform md:hidden ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <MarcaWeConnect altura={24} bolinha="#fff" />
            <span className="wc-elo-big">ELO<span className="wc-dot">.</span></span>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="close-mobile-menu-btn"
            className="text-white/70 hover:text-white hover:bg-white/10"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {groupedNav.map((grp, gi) => (
            <div key={grp.group} className={gi > 0 ? 'mt-3' : ''}>
              <p className="wc-grp">
                {grp.group}
              </p>
              <div className="space-y-1">
                {grp.items.map((item) => renderNavItem(item, true))}
              </div>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40 flex items-center justify-between px-4 md:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden"
              onClick={() => setMobileMenuOpen(true)}
              data-testid="open-mobile-menu-btn"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <div className="hidden sm:flex items-center gap-3">
              <MarcaWeConnect altura={30} />
              <span className="wc-brand-elo">ELO<span className="wc-dot">.</span></span>
              <span className="wc-brand-sep"></span>
              <span className="wc-brand-sys">{isPreview ? 'Ambiente de Teste' : 'Sistema de Atendimentos'}</span>
              {nomeCurto(user?.name) && (
                <span className="wc-saudacao hidden lg:inline-flex" data-testid="saudacao-topo">
                  Olá {nomeCurto(user?.name)}, {saudacaoDaHora(agora)}!
                </span>
              )}
              {isPreview && (
                <span className="px-2 py-0.5 text-xs font-medium bg-amber-100 text-amber-800 rounded-full border border-amber-300">
                  TESTE
                </span>
              )}
              {isConsulta && (
                <span className="px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 rounded-full border border-blue-300">
                  👁️ Somente consulta
                </span>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {/* Notificações — oculto para perfil consulta */}
            {!isConsulta && <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="relative"
                  data-testid="notifications-btn"
                >
                  <Bell className="h-5 w-5" />
                  {notificacoesNaoLidas > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center">
                      {notificacoesNaoLidas > 9 ? '9+' : notificacoesNaoLidas}
                    </span>
                  )}
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80 p-0">
                <div className="flex items-center justify-between p-3 border-b">
                  <h4 className="font-semibold">Notificações</h4>
                  {notificacoesNaoLidas > 0 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={marcarTodasComoLidas}
                      className="text-xs"
                    >
                      <Check className="h-3 w-3 mr-1" />
                      Marcar todas lidas
                    </Button>
                  )}
                </div>
                <div className="max-h-80 overflow-y-auto">
                  {notificacoes.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8 text-sm">
                      Nenhuma notificação
                    </p>
                  ) : (
                    notificacoes.slice(0, 10).map((notif) => (
                      <div
                        key={notif.id}
                        className={`p-3 border-b hover:bg-muted/50 cursor-pointer transition-colors ${
                          !notif.lida ? 'bg-blue-50 dark:bg-blue-950/20' : ''
                        }`}
                        onClick={() => {
                          if (!notif.lida) marcarComoLida(notif.id);
                          setNotificacaoSelecionada(notif);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          {!notif.lida && (
                            <span className="h-2 w-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{notif.titulo}</p>
                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                              {notif.mensagem?.split('\n')[0]}
                            </p>
                            <p className="text-xs text-muted-foreground mt-2">
                              {new Date(notif.data_criacao).toLocaleString('pt-BR')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <div className="p-2 border-t">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="w-full text-xs text-blue-600 hover:text-blue-700"
                    onClick={() => abrirVerTodas()}
                  >
                    Ver todas as notificações
                  </Button>
                </div>
              </PopoverContent>
            </Popover>}

            {/* Modal: TODAS as notificações */}
            <Dialog open={verTodasOpen} onOpenChange={(open) => { if (!open) setVerTodasOpen(false); }}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="flex items-center justify-between pr-6">
                    <span>Todas as notificações ({todasTotal})</span>
                  </DialogTitle>
                  <DialogDescription asChild>
                    <div className="flex items-center gap-2 pt-1">
                      <Button
                        variant={filtroNaoLidas ? 'default' : 'outline'}
                        size="sm"
                        className="h-7 text-xs"
                        onClick={() => { const novo = !filtroNaoLidas; setFiltroNaoLidas(novo); abrirVerTodas(novo); }}
                      >
                        Só não lidas ({notificacoesNaoLidas})
                      </Button>
                      {notificacoesNaoLidas > 0 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 text-xs"
                          onClick={async () => { await marcarTodasComoLidas(); abrirVerTodas(); }}
                        >
                          <Check className="h-3 w-3 mr-1" /> Marcar todas lidas
                        </Button>
                      )}
                    </div>
                  </DialogDescription>
                </DialogHeader>
                <div className="max-h-[60vh] overflow-y-auto -mx-6 px-6 divide-y">
                  {carregandoTodas ? (
                    <p className="text-center text-muted-foreground py-8 text-sm">Carregando…</p>
                  ) : todasNotifs.length === 0 ? (
                    <p className="text-center text-muted-foreground py-8 text-sm">Nenhuma notificação{filtroNaoLidas ? ' não lida' : ''}.</p>
                  ) : (
                    todasNotifs.map((notif) => (
                      <div
                        key={notif.id}
                        className={`py-2.5 px-1 hover:bg-muted/50 cursor-pointer transition-colors ${!notif.lida ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}
                        onClick={() => {
                          if (!notif.lida) {
                            marcarComoLida(notif.id);
                            setTodasNotifs(prev => prev.map(n => n.id === notif.id ? { ...n, lida: true } : n));
                          }
                          setNotificacaoSelecionada(notif);
                        }}
                      >
                        <div className="flex items-start gap-2">
                          {!notif.lida && <span className="h-2 w-2 rounded-full bg-blue-500 mt-2 flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm truncate">{notif.titulo}</p>
                            <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notif.mensagem?.split('\n')[0]}</p>
                            <p className="text-[11px] text-muted-foreground mt-1">{new Date(notif.data_criacao).toLocaleString('pt-BR')}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </DialogContent>
            </Dialog>

            {/* Modal de Notificação Detalhada */}
            <Dialog open={!!notificacaoSelecionada} onOpenChange={(open) => !open && setNotificacaoSelecionada(null)}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="text-lg">
                    {notificacaoSelecionada?.titulo}
                  </DialogTitle>
                  <DialogDescription>
                    {notificacaoSelecionada?.data_criacao && new Date(notificacaoSelecionada.data_criacao).toLocaleString('pt-BR')}
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4 max-h-[65vh] overflow-y-auto">
                  <p className="whitespace-pre-line text-sm">
                    {notificacaoSelecionada?.mensagem}
                  </p>

                  {/* Caixa: Rodízio (o que ficou aberto da pessoa, fechamento das 7h) */}
                  <RodizioResumo r={notificacaoSelecionada?.dados_extras?.rodizio} />

                  {/* Caixa: Encerrado (canais com check do dashboard) */}
                  {notificacaoSelecionada?.dados_extras?.encerrado_detalhes?.length > 0 && (
                    <div className="mt-4 p-4 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800">
                      <p className="font-semibold text-green-800 dark:text-green-200 mb-2">
                        ✅ Encerrado ({notificacaoSelecionada.dados_extras.encerrado_detalhes.length}):
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {notificacaoSelecionada.dados_extras.encerrado_detalhes.map((d, idx) => (
                          <li key={idx} className="text-green-700 dark:text-green-300 text-sm">
                            <strong>{d.canal}</strong>
                            {d.check_por && (
                              <span className="text-green-600/80 dark:text-green-400/80">
                                {' '}— {d.check_por}
                                {d.check_hora && <> às {d.check_hora}</>}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Caixa: Em andamento (tem chamados mas sem check) */}
                  {notificacaoSelecionada?.dados_extras?.em_andamento_detalhes?.length > 0 && (
                    <div className="mt-4 p-4 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800">
                      <p className="font-semibold text-amber-800 dark:text-amber-200 mb-2">
                        🕐 Em andamento ({notificacaoSelecionada.dados_extras.em_andamento_detalhes.length}):
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {notificacaoSelecionada.dados_extras.em_andamento_detalhes.map((d, idx) => (
                          <li key={idx} className="text-amber-700 dark:text-amber-300 text-sm">
                            <strong>{d.canal}</strong>
                            {d.atendimentos > 0 && (
                              <span className="text-amber-600/80 dark:text-amber-400/80">
                                {' '}— {d.atendimentos} atendimento{d.atendimentos > 1 ? 's' : ''}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Caixa: Sem atividade */}
                  {notificacaoSelecionada?.dados_extras?.canais_sem_atividade?.length > 0 && (
                    <div className="mt-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700">
                      <p className="font-semibold text-slate-700 dark:text-slate-300 mb-2">
                        ⚠️ Sem atividade ({notificacaoSelecionada.dados_extras.canais_sem_atividade.length}):
                      </p>
                      <ul className="list-disc list-inside space-y-1">
                        {notificacaoSelecionada.dados_extras.canais_sem_atividade.map((canal, idx) => (
                          <li key={idx} className="text-slate-600 dark:text-slate-400 text-sm">{canal}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Caixa: Demais atividades (gestao) */}
                  {notificacaoSelecionada?.dados_extras?.atividades_demais?.length > 0 && (
                    <div className="mt-4 p-4 rounded-lg bg-indigo-50 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-800">
                      <p className="font-semibold text-indigo-800 dark:text-indigo-200 mb-2">🗂️ Demais atividades:</p>
                      <ul className="space-y-1">
                        {notificacaoSelecionada.dados_extras.atividades_demais.map((a, idx) => (
                          <li key={idx} className="text-sm flex items-start gap-2">
                            <span>{a.verificada_hoje ? '✅' : '⚠️'}</span>
                            <span>
                              <strong className="text-indigo-800 dark:text-indigo-200">{a.atividade}</strong>
                              <span className="text-muted-foreground">
                                {a.verificada_hoje ? ` — verificada${a.verificada_por ? ' por ' + a.verificada_por : ''}` : ' — não verificada hoje'}
                              </span>
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Caixa: Atividade por pessoa (hoje) */}
                  {notificacaoSelecionada?.dados_extras?.por_pessoa?.length > 0 && (
                    <div className="mt-4 p-4 rounded-lg bg-slate-50 dark:bg-slate-900/30 border border-slate-200 dark:border-slate-700">
                      <p className="font-semibold text-slate-700 dark:text-slate-300 mb-2">👥 Atividade por pessoa (hoje):</p>
                      <ul className="space-y-2">
                        {notificacaoSelecionada.dados_extras.por_pessoa.map((p, idx) => (
                          <li key={idx} className="text-sm">
                            <strong className="text-slate-700 dark:text-slate-200">{p.pessoa}</strong>
                            <div className="text-muted-foreground text-xs mt-0.5 space-y-0.5">
                              {p.atendimentos_abertos > 0 && <div>• {p.atendimentos_abertos} atendimento(s) aberto(s)</div>}
                              {p.canais_verificados?.length > 0 && <div>• Canais: {p.canais_verificados.join(', ')}</div>}
                              {p.atividades_verificadas?.length > 0 && <div>• Atividades: {p.atividades_verificadas.join(', ')}</div>}
                              {!p.atendimentos_abertos && !(p.canais_verificados?.length) && !(p.atividades_verificadas?.length) && <div>• sem registros</div>}
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </DialogContent>
            </Dialog>

            <MinhasDemandas />

            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-btn"
            >
              {theme === 'dark' ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="user-menu-btn">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                      {getInitials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium">{user?.name}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/perfil')} data-testid="profile-menu-item">
                  <User className="h-4 w-4 mr-2" />
                  Perfil
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                  <LogOut className="h-4 w-4 mr-2" />
                  Sair
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
